import { useMemo } from 'preact/hooks';

import { useSelector } from '@/redux/helper';
import { selectTourGate } from '@/redux/slices/tours/selectors';

import { useTourContext } from '../context';
import arbitrate from '../core/arbitrate';
import { TOURS } from '../registry';
import { TourRegistration } from '../types';

/**
 * Hook wrapper over the pure `arbitrate`. Picks which tour surfaces on the current route.
 *
 * Subscribes to `selectTourGate` — a memoized slice of the store — plus the page-supplied tour
 * context, so an unrelated dispatch still cannot wake the arbiter. Re-arbitration happens when
 * tour progress, the route, or the published context changes.
 *
 * The context IS the re-evaluation signal: a page that has resolved its data republishes, and
 * this recomputes. There is no separate "notify the host" call for a future code path to forget.
 */
const useTourArbiter = (pathname: string): TourRegistration | undefined => {
  const gate = useSelector(selectTourGate);
  const context = useTourContext();
  return useMemo(() => arbitrate(TOURS, gate, pathname, context), [gate, pathname, context]);
};

export default useTourArbiter;
